import React from 'react';
import { ButtonGroup, Button } from 'ui';
import request from 'utils/request_ajax';
import { routes } from 'constants/config';
import { createCompareFn } from 'utils/sort';
import Table from 'components/shared/table';
import LinkButton from 'components/shared/LinkButton';
import Loadable from 'components/shared/loadable';
import SettingsHeading from 'components/settings/SettingsHeading';
import EditRole from './EditRole';

const standardRoleNames = ['Admin', 'Manager', 'Creator', 'Learner'];

const newRole = {
  id: null,
  name: null,
  editable: true,
  has_users: false,
  default_role_mapping: 'custom',
};

const RoleCounter = ({ roleCount, maxRoleCount, ...props }) => {
  return (
    <span {...props}>
      ({roleCount}/{maxRoleCount})
    </span>
  );
};

const RoleTable = ({ roles, onRoleClick }) => (
  <Table alternate>
    <Table.Body>
      {roles.map(role => (
        <Table.Row key={role.id}>
          <Table.Body.Cell className="RoleIndex-roleName" size="s">
            {role.name}
          </Table.Body.Cell>
          <Table.Body.Cell size="s">
            <LinkButton
              className="Role-editLink"
              onClick={() => onRoleClick(role)}
            >
              {role.editable
                ? t('settings.tab.roles.detail_page_link.editable')
                : t('settings.tab.roles.detail_page_link.non_editable')}
            </LinkButton>
          </Table.Body.Cell>
        </Table.Row>
      ))}
    </Table.Body>
  </Table>
);

const LoadingTable = () => (
  <Table alternate>
    <Table.Body>
      {standardRoleNames.map(name => (
        <Table.Row key={name}>
          <Table.Body.Cell className="RoleIndex-roleName" size="s">
            {name}
          </Table.Body.Cell>
          <Table.Body.Cell size="s" />
        </Table.Row>
      ))}
    </Table.Body>
  </Table>
);

const RoleIndex = ({
  standardRoles,
  customRoles,
  canAddNewRole,
  onRoleClick,
  features,
  loading,
  maxTotalRoleCount,
}) => {
  return (
    <>
      <SettingsHeading>{t('settings.tab.roles.title')}</SettingsHeading>
      <p>{t('settings.tab.roles.description')}</p>
      <h2 className="ff-ss fw-b fz-16">
        {t('settings.tab.roles.standard_roles')}
      </h2>
      {loading && !standardRoles.length ? (
        <LoadingTable />
      ) : (
        <RoleTable roles={standardRoles} onRoleClick={onRoleClick} />
      )}
      {!!customRoles.length && (
        <div className="mt-10">
          <h2 className="ff-ss fw-b fz-16">
            {t('settings.tab.roles.custom_roles')}
            <RoleCounter
              className="ml-4"
              roleCount={customRoles.length || 0}
              maxRoleCount={maxTotalRoleCount - standardRoles.length}
            />
          </h2>
          <RoleTable roles={customRoles} onRoleClick={onRoleClick} />
        </div>
      )}
      {features.hasCustomRoles && (
        <ButtonGroup alignEnd>
          <Button
            className="mt-8"
            onClick={() => onRoleClick(newRole)}
            disabled={loading || !canAddNewRole}
            customColor
            data-toggle={!canAddNewRole ? 'tooltip' : 'noop'}
            data-title={t('settings.tab.roles.new.disabled_tooltip')}
            data-placement="top"
          >
            {t('settings.tab.roles.new.title')}
          </Button>
        </ButtonGroup>
      )}
    </>
  );
};

class Roles extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      standardRoles: [],
      customRoles: [],
      currentlyEditing: null,
      loading: true,
    };

    this.handleRoleTabClick = this.handleRoleTabClick.bind(this);
  }

  componentDidMount() {
    this.navBarLink = document.querySelector("a[href='#roles']");
    this.navBarLink.addEventListener('click', this.handleRoleTabClick);

    this.getRoles();
  }

  componentWillUnmount() {
    this.navBarLink.removeEventListener('click', this.handleRoleTabClick);
  }

  handleRoleTabClick(e) {
    this.setState({ currentlyEditing: null });
  }

  getRoles() {
    request.get(routes.ROLES).then(roles => {
      const standardRoles = [];
      const otherRoles = [];
      roles.forEach(role => {
        if (standardRoleNames.includes(role.name)) {
          standardRoles.push(role);
        } else {
          otherRoles.push(role);
        }
      });
      const customRoles = otherRoles.sort(createCompareFn('name'));
      this.setState({ standardRoles, customRoles, loading: false });
    });
  }

  canAddNewRole() {
    return (
      this.state.standardRoles.length + this.state.customRoles.length <
      this.props.meta.maxTotalRoleCount
    );
  }

  editRole(role) {
    this.setState({ currentlyEditing: role });
  }

  cancelEdit() {
    this.editRole(null);
    this.setState({ loading: true });
    this.getRoles();
  }

  render() {
    const editing = this.state.currentlyEditing;

    return (
      <div>
        {editing && (
          <EditRole
            role={editing}
            features={this.props.meta.features}
            onCancel={() => this.cancelEdit()}
          />
        )}
        {!editing && (
          <Loadable loading={this.state.loading}>
            <RoleIndex
              standardRoles={this.state.standardRoles}
              customRoles={this.state.customRoles}
              onRoleClick={role => this.editRole(role)}
              features={this.props.meta.features}
              loading={this.state.loading}
              canAddNewRole={this.canAddNewRole()}
              maxTotalRoleCount={this.props.meta.maxTotalRoleCount}
            />
          </Loadable>
        )}
      </div>
    );
  }
}

export default Roles;
